import { Hash, Layers, Tag, Target, Users } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useExamples, useNews } from '../hooks/useContent';
import { audienceLabels, complexityLabels, goalLabels, topicLabels } from '../lib/router';

export function TagsPage() {
  const { posts } = useNews();
  const { examples } = useExamples();

  // Count content by tag
  const getTagCounts = () => {
    const counts: Record<string, { news: number; examples: number; type: string }> = {};

    // Count topics in news
    posts.forEach(post => {
      post.topics.forEach(topic => {
        if (!counts[topic]) counts[topic] = { news: 0, examples: 0, type: 'topic' };
        counts[topic].news++;
      });
    });

    // Count categories in examples
    examples.forEach(example => { 
      // Steps
      // if (example.steps) {
      //   example.steps.forEach(step => {
      //     if (!counts[step]) counts[step] = { news: 0, examples: 0, type: 'topic' };
      //     counts[step].examples++;
      //   });
      // }
      
      // Audience
      if (!counts[example.audience]) counts[example.audience] = { news: 0, examples: 0, type: 'audience' };
      counts[example.audience].examples++;
      
      // Complexity
      if (!counts[example.complexity]) counts[example.complexity] = { news: 0, examples: 0, type: 'complexity' };
      counts[example.complexity].examples++;
      
      // Goal
      if (!counts[example.goal]) counts[example.goal] = { news: 0, examples: 0, type: 'goal' };
      counts[example.goal].examples++;
    });

    return counts;
  };

  const tagCounts = getTagCounts();

  const getTagIcon = (type: string) => {
    switch (type) {
      case 'topic': return <Hash className="h-4 w-4" />;
      case 'audience': return <Users className="h-4 w-4" />;
      case 'complexity': return <Layers className="h-4 w-4" />;
      case 'goal': return <Target className="h-4 w-4" />;
      default: return <Tag className="h-4 w-4" />;
    }
  };

  const getTagLabel = (tag: string) => {
    return topicLabels[tag] || 
           audienceLabels[tag] || 
           complexityLabels[tag] || 
           goalLabels[tag] || 
           tag.replace('-', ' ');
  };

  // Group tags by type
  const groupedTags = Object.entries(tagCounts).reduce((acc, [tag, data]) => {
    if (!acc[data.type]) acc[data.type] = [];
    acc[data.type].push({ tag, ...data });
    return acc;
  }, {} as Record<string, Array<{ tag: string; news: number; examples: number; type: string }>>);

  // Sort tags by total content count
  Object.keys(groupedTags).forEach(type => {
    groupedTags[type].sort((a, b) => (b.news + b.examples) - (a.news + a.examples));
  });

  return (
    <>
      <Helmet>
        <title>Browse Tags - Promptliteracy.ai</title>
        <meta name="description" content="Explore all tags and categories to find specific content about AI literacy, prompt engineering, and educational technology." />
        <meta property="og:title" content="Browse Tags - Promptliteracy.ai" />
        <meta property="og:description" content="Explore all tags and categories to find specific content about AI literacy, prompt engineering, and educational technology." />
      </Helmet>

      {/* Header Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Tag className="h-8 w-8" />
              <h1 className="text-4xl font-bold">Browse Tags</h1>
            </div>
            <p className="text-xl opacity-90 mb-8">
              Explore our content by topics, audience, complexity, and learning goals
            </p>
            
            <div className="flex items-center justify-center gap-6 text-sm opacity-90">
              <div className="flex items-center gap-2">
                <Hash className="h-4 w-4" />
                <span>Topics</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>Audience</span>
              </div>
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4" />
                <span>Complexity</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                <span>Goals</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container py-8">
        {/* Tag Groups */}
        <div className="space-y-8">
          {Object.entries(groupedTags).map(([type, tags]) => (
            <Card key={type} className="card-slate hover:shadow-lg transition-all border-l-4 border-l-primary/30 hover:border-l-primary card-accent">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl text-primary">
                  {getTagIcon(type)}
                  {type.charAt(0).toUpperCase() + type.slice(1)}s
                  <Badge variant="outline" className="ml-2">
                    {tags.length} tags
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {tags.map(({ tag, news, examples }) => (
                    <Link
                      key={tag}
                      to={`/tag/${tag}`}
                      className="block group"
                    >
                      <div className="p-4 rounded-xl border transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer bg-gradient-to-br from-white to-slate-50 border-slate-200 hover:border-slate-300 group">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 group-hover:text-slate-600 transition-colors">
                              {getTagIcon(type)}
                            </span>
                            <span className="font-medium text-slate-700 group-hover:text-slate-900 transition-colors">
                              {getTagLabel(tag)}
                            </span>
                          </div>
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-xs font-semibold text-slate-600 group-hover:bg-slate-200 transition-colors">
                            {news + examples}
                          </div>
                        </div>
                        <div className="text-xs text-slate-500">
                          {news > 0 && <span>{news} article{news !== 1 ? 's' : ''}</span>}
                          {news > 0 && examples > 0 && <span className="mx-1">•</span>}
                          {examples > 0 && <span>{examples} example{examples !== 1 ? 's' : ''}</span>}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Popular Tags Cloud */}
        <Card className="mt-12 card-slate hover:shadow-lg transition-all border-l-4 border-l-secondary/30 hover:border-l-secondary card-accent">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <Tag className="h-5 w-5" />
              Popular Tags
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(tagCounts)
                .sort(([,a], [,b]) => (b.news + b.examples) - (a.news + a.examples))
                .slice(0, 20)
                .map(([tag, { news, examples }]) => {
                  const total = news + examples;
                  const size = total > 10 ? 'text-lg' : total > 5 ? 'text-base' : 'text-sm';
                  const weight = total > 10 ? 'font-bold' : total > 5 ? 'font-medium' : 'font-normal';
                  
                  return (
                    <Link
                      key={tag}
                      to={`/tag/${tag}`}
                      className={`
                        inline-block px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 
                        hover:bg-slate-200 hover:text-slate-900 transition-all border border-slate-200
                        hover:border-slate-300 hover:shadow-sm
                        ${size} ${weight}
                      `}
                    >
                      {getTagLabel(tag)}
                      <span className="ml-1 text-xs opacity-60">({total})</span>
                    </Link>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}